import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { WorkspaceLayout } from '../../components/layout/WorkspaceLayout';
import { WorkspaceTopBar } from '../../components/layout/WorkspaceTopBar';
import { ChatPanel } from '../../features/chat/ChatPanel';
import { PreviewPanel } from '../../features/preview/PreviewPanel';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { fetchChatMessages, hydrateProjectChat, sendMessage } from '../../redux/slices/chatSlice';
import { ensureProjectChats, setActiveChatId } from '../../redux/slices/chatsSlice';
import { runProjectSandbox } from '../../redux/slices/previewSlice';
import { fetchProjectById, setSelectedProjectId } from '../../redux/slices/projectsSlice';
import { PublicProjectDeploymentWarning } from '../../features/workspace/PublicProjectDeploymentWarning';
import { ProjectFeaturesModal } from '../../features/workspace/ProjectFeaturesModal';
import { downloadProjectZipApi } from '../../api/agent.api';
import { DEFAULT_LLM_MODEL_KEY, getLlmModelByKey } from '../../constants/llmModels';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';
import { DEFAULT_LLM_PROVIDER_KEY } from '../../constants/llmProviders';
import {
  buildFeatureActivationPrompt
} from '../../utils/featureActivationPrompts';
import { classNames } from '../../utils/classNames';
import { hasProjectFeature } from '../../utils/projectFeatures';
import { hasPreviewFiles } from '../../utils/previewFiles';

const SkeletonBlock = ({ className = '' }) => (
  <div className={`animate-pulse rounded-lg bg-neutral-200 dark:bg-white/10 ${className}`} />
);

const WorkspacePageSkeleton = () => (
  <AppShell
    variant="workspace"
    workspaceHeader={
      <div className="flex h-12 items-center gap-3 border-b border-neutral-200 px-3 dark:border-border">
        <SkeletonBlock className="h-7 w-7 rounded-lg" />
        <SkeletonBlock className="h-4 w-32" />
        <div className="ms-auto flex gap-2">
          <SkeletonBlock className="h-8 w-20 rounded-lg" />
          <SkeletonBlock className="h-8 w-24 rounded-lg" />
        </div>
      </div>
    }
  >
    <div className="grid h-full min-h-0 grid-cols-1 xl:grid-cols-[minmax(320px,380px)_minmax(0,1fr)]">
      <section className="flex h-full min-h-0 flex-col border-e border-neutral-200 dark:border-border">
        <div className="flex-1 space-y-4 overflow-hidden px-4 py-4">
          <SkeletonBlock className="h-16 w-[85%] rounded-2xl" />
          <SkeletonBlock className="h-24 w-full rounded-xl" />
          <SkeletonBlock className="h-20 w-[90%] rounded-xl" />
        </div>
        <div className="border-t border-neutral-100 p-3 dark:border-border">
          <SkeletonBlock className="h-12 w-full rounded-full" />
        </div>
      </section>
      <section className="hidden h-full min-h-0 xl:block">
        <div className="flex h-full items-center justify-center">
          <SkeletonBlock className="h-8 w-48" />
        </div>
      </section>
    </div>
  </AppShell>
);

export const WorkspacePage = () => {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t, language } = useI18n();
  const { models: catalogModels } = useLlmCatalog();
  const [bootstrapChat] = useState(() => location.state?.bootstrapChat || null);
  const [panelMode, setPanelMode] = useState('preview');
  const [mobilePane, setMobilePane] = useState('tasks');
  const [workspaceStatus, setWorkspaceStatus] = useState('loading');
  const [featuresModalOpen, setFeaturesModalOpen] = useState(false);
  const [focusFeature, setFocusFeature] = useState(null);
  const [pendingActivationPrompt, setPendingActivationPrompt] = useState(null);
  const { items } = useAppSelector((state) => state.projects);
  const { activeChatIdByProjectId, loading: chatsLoading } = useAppSelector((state) => state.chats);
  const project = items.find((item) => item.id === projectId) || null;
  const activeChatId = activeChatIdByProjectId[projectId] || null;

  const openActivationModal = useCallback((featureKey) => {
    setFocusFeature(featureKey);
    setFeaturesModalOpen(true);
  }, []);

  const closeFeaturesModal = useCallback(() => {
    setFeaturesModalOpen(false);
    setFocusFeature(null);
  }, []);

  const sendActivationPrompt = useCallback(
    (featureKey, draft) => {
      if (!project?.id) {
        return;
      }

      const metadata =
        featureKey === 'auth'
          ? { registrationEnabled: Boolean(draft?.auth?.metadata?.registrationEnabled) }
          : {};
      const prompt = buildFeatureActivationPrompt(featureKey, metadata, t);

      if (!prompt) {
        return;
      }

      if (!activeChatId) {
        setPendingActivationPrompt({ featureKey, draft });
        return;
      }

      const model = DEFAULT_LLM_MODEL_KEY;
      const provider = getLlmModelByKey(model, catalogModels)?.provider || DEFAULT_LLM_PROVIDER_KEY;

      dispatch(
        sendMessage({
          chatId: activeChatId,
          projectId: project.id,
          projectName: project.name,
          message: prompt,
          language,
          model,
          provider
        })
      );
    },
    [activeChatId, catalogModels, dispatch, language, project?.id, project?.name, t]
  );

  const handleFeatureActivated = useCallback(
    ({ newlyEnabled, draft }) => {
      newlyEnabled.forEach((featureKey) => {
        sendActivationPrompt(featureKey, draft);
      });
    },
    [sendActivationPrompt]
  );

  useEffect(() => {
    if (!location.state?.bootstrapChat) {
      return;
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const activateFeature = location.state?.activateFeature;

    if (!activateFeature || workspaceStatus !== 'ready') {
      return;
    }

    openActivationModal(activateFeature);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate, openActivationModal, workspaceStatus]);

  useEffect(() => {
    if (!pendingActivationPrompt || !activeChatId || chatsLoading) {
      return;
    }

    sendActivationPrompt(pendingActivationPrompt.featureKey, pendingActivationPrompt.draft);
    setPendingActivationPrompt(null);
  }, [activeChatId, chatsLoading, pendingActivationPrompt, sendActivationPrompt]);

  useEffect(() => {
    if (!projectId) {
      setWorkspaceStatus('not-found');
      return undefined;
    }

    let cancelled = false;

    const initializeWorkspace = async () => {
      setWorkspaceStatus('loading');
      dispatch(setSelectedProjectId(projectId));

      let loadedProject;

      try {
        loadedProject = await dispatch(fetchProjectById(projectId)).unwrap();
      } catch (error) {
        if (!cancelled) {
          setWorkspaceStatus('not-found');
        }

        return;
      }

      if (cancelled) {
        return;
      }

      const ensureAction = await dispatch(
        ensureProjectChats({
          projectId,
          context: loadedProject.description
        })
      );

      if (cancelled) {
        return;
      }

      if (!ensureProjectChats.fulfilled.match(ensureAction)) {
        setWorkspaceStatus('ready');
        return;
      }

      const chatId = ensureAction.payload.activeChatId;

      if (chatId) {
        dispatch(hydrateProjectChat(chatId));

        try {
          await dispatch(fetchChatMessages({ chatId })).unwrap();
        } catch (error) {
          console.warn(error?.message || 'Chat history request failed.');
        }
      }

      if (!cancelled) {
        setWorkspaceStatus('ready');
      }

      if (!cancelled && hasPreviewFiles(loadedProject.files)) {
        dispatch(runProjectSandbox({ projectId }))
          .unwrap()
          .catch((error) => {
            console.warn(error?.message || 'Sandbox preview failed.');
          });
      }
    };

    initializeWorkspace();

    return () => {
      cancelled = true;
    };
  }, [dispatch, projectId]);

  const handleSelectChat = (chatId) => {
    if (!projectId || !chatId || chatId === activeChatId) {
      return;
    }

    dispatch(setActiveChatId({ projectId, chatId }));
    dispatch(hydrateProjectChat(chatId));
    dispatch(fetchChatMessages({ chatId }));
  };

  if (workspaceStatus === 'loading') {
    return <WorkspacePageSkeleton />;
  }

  if (workspaceStatus === 'not-found' || !project) {
    return <div className="p-10 text-center text-lg">{t('projectNotFound')}</div>;
  }

  const handlePublishClick = () => {
    navigate(`/workspace/${projectId}/deployments`);
  };

  const handleDownloadZip = async () => {
    try {
      const blob = await downloadProjectZipApi(projectId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${project?.name || projectId}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn(error);
    }
  };

  const handleUsersClick = () => {
    if (hasProjectFeature(project, 'auth')) {
      navigate(`/workspace/${projectId}/users`);
      return;
    }

    openActivationModal('auth');
  };

  const handleDatabaseClick = () => {
    if (hasProjectFeature(project, 'database')) {
      navigate(`/workspace/${projectId}/requests`);
      return;
    }

    openActivationModal('database');
  };

  const isPublicProject = project.visibility === 'public';
  const authEnabled = hasProjectFeature(project, 'auth');
  const databaseEnabled = hasProjectFeature(project, 'database');

  const handlePanelModeChange = (mode) => {
    setPanelMode(mode);
    setMobilePane('preview');
  };

  return (
    <WorkspaceLayout
      project={project}
      breadcrumbNotice={
        <PublicProjectDeploymentWarning projectId={projectId} isPublic={isPublicProject} />
      }
      workspaceHeader={
        <WorkspaceTopBar
          project={project}
          authEnabled={authEnabled}
          databaseEnabled={databaseEnabled}
          panelMode={panelMode}
          onPanelModeChange={handlePanelModeChange}
          onPublish={handlePublishClick}
          onRecentDeployments={handlePublishClick}
          onDownload={handleDownloadZip}
          onUsers={handleUsersClick}
          onDatabase={handleDatabaseClick}
          onSkills={() => navigate(`/workspace/${projectId}/skills`)}
          onSettings={() =>
            navigate(`/projects/${projectId}/edit`, {
              state: { returnTo: `/workspace/${projectId}` }
            })
          }
        />
      }
    >
      <div className="flex h-full min-h-0 min-w-0 flex-col xl:grid xl:grid-cols-[minmax(300px,380px)_minmax(0,1fr)]">
        <div
          className="flex shrink-0 border-b border-neutral-200 bg-white xl:hidden dark:border-border dark:bg-panel-strong"
          role="tablist"
          aria-label={t('workspace')}
        >
          {[
            { value: 'tasks', label: t('tasksList') },
            { value: 'preview', label: t('previewTab') }
          ].map((tab) => {
            const isActive = mobilePane === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setMobilePane(tab.value)}
                className={classNames(
                  'relative flex-1 py-2.5 text-sm font-medium transition',
                  isActive
                    ? 'text-neutral-950 dark:text-text'
                    : 'text-neutral-500 hover:text-neutral-800 dark:text-muted dark:hover:text-text'
                )}
              >
                {tab.label}
                {isActive ? (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-neutral-950 dark:bg-accent" />
                ) : null}
              </button>
            );
          })}
        </div>
        <div
          className={classNames(
            'min-h-0 min-w-0 overflow-hidden',
            mobilePane === 'tasks' ? 'flex flex-1 flex-col' : 'hidden',
            'xl:flex xl:h-full xl:min-h-0 xl:flex-col'
          )}
        >
          <ChatPanel
            project={project}
            activeChatId={activeChatId}
            chatsLoading={chatsLoading}
            onSelectChat={handleSelectChat}
            bootstrapChat={bootstrapChat}
          />
        </div>
        <div
          className={classNames(
            'min-h-0 min-w-0 overflow-hidden',
            mobilePane === 'preview' ? 'flex flex-1 flex-col' : 'hidden',
            'xl:flex xl:h-full xl:min-h-0 xl:flex-col'
          )}
        >
          <PreviewPanel
            project={project}
            panelMode={panelMode}
            onPanelModeChange={handlePanelModeChange}
          />
        </div>
      </div>
      <ProjectFeaturesModal
        open={featuresModalOpen}
        onClose={closeFeaturesModal}
        project={project}
        mode={focusFeature ? 'activate' : 'full'}
        focusFeature={focusFeature}
        onActivated={handleFeatureActivated}
      />
    </WorkspaceLayout>
  );
};
